/**
 * @fileoverview The Plants page's garden: the weather bar, the toolbar, and every plant as
 * cards, tiles or rows, with the add and edit form and the quick look.
 *
 * The page owns the view settings (sort, filters, view) and reads the
 * plants brief's order and marks once rather than in the toolbar or the
 * cards, because the filter,
 * the chip counts and the weather bar all have to agree, and they only can
 * if they read one answer worked out once, here.
 *
 * Where: the routes /plants and /plants/:plantObjId (scrolls to that
 * plant's card once), inside PlantsProvider.
 * API: through PlantsContext: the garden (GET /plants), add (POST
 * /plants/obj/add), edit (POST /plants/update), archive and restore (PATCH
 * /plants/:id/status); DELETE /plants/:id { confirm: 'DELETE' } (deletePlantForever,
 * an archived plant's Delete for good); GET /plants?status=0 when the status filter
 * shows the archive; the plants brief (GET /plants/brief, usePlantsBrief:
 * the "Needs you first" order, each plant's marks for the ?verdict= filter
 * and the garden's marks it is named by); the Today brief
 * (GET /today/brief, useTodayBrief: the weather bar's strip line and the
 * week's lines, which the filter sheet's "This week" chips read too, and the
 * one to-do list, whose plant jobs are said under the weather (PlantJobs;
 * D51; the week's lines, put back by the 2026-10-16 audit, went again the same
 * day: owner, "didnt we change this?"), ticked through POST /todo/marks).
 * Merge into... (D66): the Edit form's Archive panel (for a plant with twins) and the prompt after an edit that made twins open
 * the merge sheet (components/dialogs/MergePlantSheet, lib/useMergeFlow).
 * Settings: the view settings (plantViews: view and sort remembered on the
 * device, filters not); ?verdict=, ?plants= with ?for= (a week line's or a
 * Coming up rule's plants) and ?add=1 (Today's first step) in the address;
 * GRID_SCROLL_AFTER_ADD_MS (config/tunables/plantList).
 * Every "Add a plant" opens the Plants pop-up (D78, add-by-photo useAddPlant); the empty garden draws its two rows (AddWays).
 * Words (en/es): plants.addPlant, plants.noPlants (the empty page, EmptyGarden),
 * plants.list.loadFailed, plants.toolbar.* (noMatches,
 * statusArchived), plants.form.* (removeFailed, saveFailed; the archive
 * dialog's own words are ArchiveOrDeleteDialog's),
 * plants.archive.* (restore, restoreFailed, delete.*),
 * plants.form.deleteForeverEllipsis, sitewide.loading.
 * Watch out: plants added on this visit lead the list whatever the sort,
 * until the next visit. Two plants of one name are told apart by the
 * server's title on each card, row and tile (lib/plantName plantTitle: the
 * day each was added, for two in one bed; audit A36); the line under a name
 * says only what the list is sorted by.
 * A garden that could not be loaded because of a failure on our side says the
 * standard sentence (D31, lib/serverFailure) with the support address as a
 * link; the context's refusals reach the toasts with the same words.
 *
 * @module PlantGrid
 */

import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from '@/shared/components/ui/use-toast';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { Plant } from '@/shared/types/plant';
import { PlantCard } from './PlantCard';
import { UI_CONSTANTS } from '@/config/config';
import { GRID_SCROLL_AFTER_ADD_MS } from '@/config/tunables/plantList';
import { textVariants } from '@/shared/styles/variants';
import { cn } from '@/lib/utils';
import { useParams } from 'react-router-dom';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { GardenConditionsBar } from './GardenConditionsBar';
import { GuidanceQuiet, GuidanceUpdating } from '@/shared/components/GuidanceUpdating';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { PlantJobs } from './PlantJobs';
import { useTodoMarks } from '@/features/today/lib/useTodoMarks';
import { ZipRequiredBanner } from '@/shared/components/ZipRequiredBanner';
import { Trash2, Undo2 } from 'lucide-react';
import { useAddPlant, ADD_PLANT_WORDS } from '@/features/add-by-photo/useAddPlant';
import { AddWays } from '@/features/add-by-photo/AddWays';
import { useQueryClient } from '@tanstack/react-query';
import { useArchivedPlants } from '@/shared/hooks/useArchivedPlants';
import { PlantFormDialog } from './dialogs/PlantFormDialog';
import { deletePlantForever, type PlantWritePayload } from '@/shared/api/plantCatalogue';
import { DeletePlantDialog } from './DeletePlantDialog';
import { MergePlantSheet } from './dialogs/MergePlantSheet';
import { useMergeFlow } from '../lib/useMergeFlow';
import { PlantToolbar } from './PlantToolbar';
import { PlantTile } from './PlantTile';
import { PlantRow } from './PlantRow';
import { AddPlaceholder } from '@/shared/components/ui/AddPlaceholder';
import { EmptyGarden } from '@/shared/components/ui/EmptyGarden';
import { PlantQuickLook } from './PlantQuickLook';
import { ShareMoments } from '@/features/share/ShareMoments';
import { DoctorMoments } from './DoctorMoments';
import { formatDate, parseDate } from '@/shared/utils/dateUtils';
import {
  arrangePlants,
  lastActivity,
  loadViewSettings,
  type AttentionState,
  saveViewSettings,
  locationsInUse,
  stagesInUse,
  type PlantViewSettings,
  type PlantAttention,
} from '../lib/plantViews';
import { useKeepInPlace } from '../lib/keepInPlace';
import { NO_LINE_FILTER, weekLineFilter, weekLineIsFilter } from '../lib/weekLineLook';
import { usePlantsBrief } from '@/shared/hooks/usePlantsBrief';
import { useTodayBrief } from '@/shared/hooks/useTodayBrief';
import type { BriefWeekLine } from '@/shared/api/todayBrief';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { plantName } from '@/shared/lib/plantName';
import { attentionFrom } from '../lib/attentionFrom';

/**
 * The garden in one of three views: cards (one to three columns), a grid of
 * tiles, or a list of rows, each opening a plant full size. Shows loading and
 * error states while there is no garden to show.
 *
 * @example
 * ```tsx
 * // Basic usage within a parent component
 * const ParentComponent = () => {
 *   return (
 *     <div>
 *       <PlantGrid />
 *     </div>
 *   );
 * };
 * ```
 * 
 * @remarks
 * Plant data and loading state come from PlantsContext. The layout is
 * Tailwind's responsive grid classes.
 */
export const PlantGrid: React.FC = React.memo(() => {
  // State and context
  const { plants, isLoading, missingZipCode, error, addPlant: addToGarden, editPlant, archivePlant, restorePlant } = usePlantsContext();

  // null means "adding"; a plant means "editing that one". One dialog serves
  // both, so the two paths cannot drift apart.
  const [editing, setEditing] = useState<Plant | null>(null);
  /** The add or edit form is open. */
  const [isFormOpen, setIsFormOpen] = useState(false);

  /**
   * Opens the form on one plant, to edit it.
   *
   * @param plant - The plant.
   */
  const openEdit =(plant: Plant): void => { setEditing(plant); setIsFormOpen(true); };

  /*
   * The plant a tile or a row has been opened to full size, or null.
   *
   * The compact views show a picture, a name and a bed, which is enough to
   * find a plant and not enough to do anything with it. Opening one gives back
   * the card — the photographs, the forecast, the journal.
   */
  const [opened, setOpened] = useState<Plant | null>(null);

  /**
   * Follow the opened plant as the garden changes underneath it.
   *
   * `opened` holds a plant object from one render of the query. Add a photo
   * from inside the quick look and the garden refetches into new objects,
   * leaving this one frozen at the moment it was opened — the photograph would
   * appear on the card behind and not on the one in front of you.
   */
  const openedPlant = useMemo(
    () => (opened ? plants?.find((p) => p.plant_obj_id === opened.plant_obj_id) ?? opened : null),
    [opened, plants]
  );

  // Declared before the handlers that close over it. Those are only called
  // later, so the old order worked — but a const referenced twenty lines above
  // its declaration reads as a bug whether or not it is one.
  const { reactTranslate: t } = useTranslation();

  /** Plants added on this visit, newest first: they lead the list until the next visit. */
  const [justAdded, setJustAdded] = useState<number[]>([]);
  const addPlant = useAddPlant({
    onType: () => { setEditing(null); setIsFormOpen(true); },
    onFiled: ({ plantObjIds }) => setJustAdded((ids) => [...plantObjIds, ...ids.filter((id) => !plantObjIds.includes(id))]),
  });
  /** Opens the Plants pop-up, to add a plant. */
  const openAdd = (): void => addPlant.open();

  /**
   * Archives a plant, for the shared Archive or delete dialog (the edit form's); a failure is thrown for the form to show.
   *
   * @param plantObjId - The plant.
   */
  const handleRemove = async (plantObjId: number): Promise<void> => {
    const result = await archivePlant(plantObjId);
    if (!result.success) throw new Error(result.error ?? t('plants.form.removeFailed'));
  };

  /** Merge into... (D66): the sheet's state, and the prompt raised after an edit that made twins. */
  const merge = useMergeFlow();

  /**
   * Saves the plant; a new one's id comes back, so its first stage can be recorded.
   *
   * @param payload - Its variety and bed.
   * @param plantObjId - The plant being edited, or null to add one.
   * @returns The plant's id, or null when a new one's was not reported.
   */
  const handleSave = async (payload: PlantWritePayload, plantObjId: number | null): Promise<number | null> => {
    if (plantObjId === null) {
      const result = await addToGarden(payload);
      if (!result.success) throw new Error(result.error ?? t('plants.form.saveFailed'));
      const added = result.plant_obj_id != null ? Number(result.plant_obj_id) : null;
      if (added !== null) {
        setJustAdded((ids) => [added, ...ids.filter((id) => id !== added)]);
        // After the form has closed and let go of the page's scroll.
        window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), GRID_SCROLL_AFTER_ADD_MS);
      }
      return added;
    }
    const result = await editPlant(plantObjId, payload);
    if (!result.success) throw new Error(result.error ?? t('plants.form.saveFailed'));
    merge.rememberEdit(plantObjId, result);
    return plantObjId;
  };

  /** /plants/:plantObjId: the card to scroll to on arrival. */
  const { plantObjId } = useParams();
  /** Whose archive, for its query. */

  /*
   * How this gardener is looking at the garden right now.
   *
   * Held here rather than in the toolbar because the grid is what the settings
   * are about; the toolbar only shows them and reports changes. View and sort
   * come back from the last visit, filters deliberately do not — see
   * loadViewSettings.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  // A verdict handed over from Today or the weather page, or the plants a
  // Coming up rule's line is about (?plants=, named by ?for=), arrives in the
  // address and is taken as the filter. The address then follows the filter,
  // so a reload or a shared link keeps it, and clearing it clears the address;
  // it used to be dropped at once, so a reload lost what Today had opened.
  const [settings, setSettings] = useState<PlantViewSettings>(() => {
    const verdict = searchParams.get('verdict');
    const plantIds = (searchParams.get('plants') ?? '').split(',').filter((id) => /^\d+$/.test(id)).join(',');
    if (verdict) return { ...loadViewSettings(), verdict, status: 'active' };
    if (plantIds) return { ...loadViewSettings(), plantIds, plantsFor: searchParams.get('for') ?? '', status: 'active' };
    return loadViewSettings();
  });
  useEffect(() => {
    const wanted: Record<string, string> = {
      verdict: settings.verdict || '',
      plants: settings.plantIds || '',
      for: settings.plantIds ? settings.plantsFor || '' : '',
    };
    const next = new URLSearchParams(searchParams);
    let changed = false;
    for (const [key, value] of Object.entries(wanted)) {
      if ((next.get(key) ?? '') === value) continue;
      if (value) next.set(key, value); else next.delete(key);
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
  }, [settings.verdict, settings.plantIds, settings.plantsFor, searchParams, setSearchParams]);
  // ?add=1, from Today's first step: the Plants pop-up, open. It leaves the
  // address too, so a reload or Back does not open it again.
  useEffect(() => {
    if (searchParams.get('add') !== '1') return;
    addPlant.open();
    const next = new URLSearchParams(searchParams);
    next.delete('add');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, addPlant]);

  /** Merges one changed setting into the rest; stable, so children can hold on to it. */
  const updateSettings = useCallback((patch: Partial<PlantViewSettings>): void => {
    setSettings((current) => ({ ...current, ...patch }));
  }, []);

  /*
   * Remembering happens here, not inside the state updater.
   *
   * saveViewSettings used to be called from within setSettings' updater, which
   * is a side effect in a function React is entitled to call more than once —
   * and does, twice per update, under StrictMode. Writing to localStorage from
   * there is the kind of thing that works until the day it doesn't. An effect
   * on the settled value runs once and says what it means.
   */
  useEffect(() => { saveViewSettings(settings); }, [settings]);

  /*
   * The archive, fetched only when somebody asks to see it.
   *
   * Removing a plant sets its status rather than deleting it, so the archive
   * is the same garden under a different filter — but it is a different query,
   * because the server decides what counts as still growing.
   */
  const wantsArchived = settings.status === 'archived' || settings.status === 'all';
  const { data: archivedPlants = [] } = useArchivedPlants(wantsArchived);

  /** Plants no longer in the garden, so the grid can mark them as such. */
  const archivedIds = useMemo(
    () => new Set(archivedPlants.map((plant) => plant.plant_obj_id)),
    [archivedPlants]
  );

  /** The plants the status filter draws from: the garden, the archive, or both. */
  const pool = useMemo(() => {
    if (settings.status === 'archived') return archivedPlants;
    if (settings.status === 'all') return [...(plants ?? []), ...archivedPlants];
    return plants;
  }, [settings.status, plants, archivedPlants]);

  /** The query cache, to read the garden and the archive again after a change. */
  const queryClient = useQueryClient();
  /** The archived plant being deleted for good, while its type-DELETE confirm is open. */
  const [deleting, setDeleting] = useState<Plant | null>(null);
  /** True while the delete is on its way. */
  const [deleteBusy, setDeleteBusy] = useState(false);
  /** Why the last delete failed, said in the dialog. */
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /**
   * Opens the type-DELETE confirm for an archived plant.
   *
   * @param plant - The archived plant.
   */
  const askDelete = (plant: Plant): void => { setDeleteError(null); setDeleting(plant); };

  /** Deletes the plant in the dialog for good, with its photos and notes; the dialog stays, with the error, if it fails. */
  const handleDeleteForever = async (): Promise<void> => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deletePlantForever(deleting.plant_obj_id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plants'] }),
        queryClient.invalidateQueries({ queryKey: ['archived-plants'] }),
      ]);
      setDeleting(null);
    } catch (err) {
      setDeleteError(errorMessage(err, t('plants.archive.delete.failed')));
    } finally {
      setDeleteBusy(false);
    }
  };

  /** The plant whose Restore is on its way, so its button cannot be pressed twice. */
  const [restoringId, setRestoringId] = useState<number | null>(null);

  /**
   * Puts a plant back in the garden, from wherever it is being shown.
   *
   * Through the context, which is what archiving already did. Written out here
   * it invalidated the two plant lists and not the outlook, so a restored
   * plant came back with no forecast on its card, no part in the alert count
   * and nothing in the bar above the garden.
   *
   * The context reports a refusal rather than throwing; it is said in a
   * toast, as the archive's own Restore says it. It used to stop the spinner
   * and nothing else.
   *
   * @param plant - The archived plant.
   */
  const handleRestore = async (plant: Plant): Promise<void> => {
    setRestoringId(plant.plant_obj_id);
    try {
      const result = await restorePlant(plant.plant_obj_id);
      if (!result.success) {
        toast({ title: result.error || t('plants.archive.restoreFailed'), variant: 'destructive' });
      }
    } finally {
      setRestoringId(null);
    }
  };

  /*
   * The brief, the one read Today makes (useTodayBrief): the weather bar's
   * strip line and the week's lines, finished by the backend, which the
   * filter sheet's "This week" chips read too.
   */
  const { brief, isLoading: briefLoading, isError: briefFailed, noZip: briefNoZip, queryKey: briefKey, ask } = useTodayBrief();
  /** The one to-do list (D51), when the brief has it: the top of Plants says the week as jobs. */
  const todo = brief?.hasOutlook ? brief.todo : null;
  /** Ticking a job here, as on Today. */
  const todoMarks = useTodoMarks(briefKey, ask);
  /** The week's lines, the list's (todo.week.lines: the week by condition, the water line last), in its order. */
  const weekLines = useMemo((): BriefWeekLine[] => brief?.todo.week.lines ?? [], [brief]);
  /*
   * The plants brief (usePlantsBrief, GET /plants/brief; D40): the "Needs you
   * first" order and each plant's marks, decided by the backend. The chip,
   * the filter and the order all read this one answer.
   *
   * Null until it is in: an order or a filter it cannot answer yet is not
   * applied. The marks are null without an outlook (the request failing, or
   * no postal code), so the verdict filter never manufactures a confident
   * empty garden from a forecast it never saw.
   */
  const { brief: plantsBrief, marks } = usePlantsBrief();
  /** The brief's order as places and its marks by plant, for arrangePlants; null until the brief is in. */
  const attention = useMemo((): PlantAttention | null => attentionFrom(plantsBrief), [plantsBrief]);

  /**
   * Whether the question "what needs attention?" has any meaning right now.
   *
   * It does not while the archive is what is on screen: everything there has
   * been taken out of the garden, so nothing in it can need attending to, and
   * a bar naming the garden's week over a list of plants that are no longer
   * in it would be answering a question nobody asked.
   */
  const attentionApplies = settings.status !== 'archived';

  /**
   * What the weather bar shows under the weather, and why it has nothing when
   * it has nothing: the week once the brief is in (with a forecast behind
   * it); its room held while the brief is on its way; nothing when nothing
   * is coming (no postal code to ask with, a brief that failed, no forecast
   * to be had, or the archive on screen). A thing that is not coming should
   * not wear "Checking".
   */
  const weekState: AttentionState = (() => {
    // No postcode Grow can plan from (none, or one it cannot use): no brief is coming.
    if (!attentionApplies || missingZipCode || briefNoZip || briefFailed) return 'unavailable';
    if (brief !== undefined) return brief.hasOutlook ? 'known' : 'unavailable';
    return briefLoading ? 'checking' : 'unavailable';
  })();

  /** The week line the garden is narrowed to (its plants, named by it), or null. */
  const activeLine = useMemo(
    () => weekLines.find((line) => weekLineIsFilter(line, settings)) ?? null,
    [weekLines, settings],
  );

  /** The job the garden is narrowed to (its plants, named by its filter's words), or null. */
  const activeJob = useMemo(
    () => todo?.jobs.find((job) => job.filter && weekLineIsFilter({ plantIds: job.filter.plantIds, title: job.filter.text }, settings)) ?? null,
    [todo, settings],
  );

  /**
   * Whether the bar names the garden's filter now: the week is known and the
   * filter is one of its lines, which then says "Show all". Then the toolbar
   * leaves out its own chip for it.
   */
  const barNamesLine = weekState === 'known' && (activeLine !== null || (todo !== null && activeJob !== null));


  /**
   * Plants added on this visit lead the list, newest first, whatever the sort,
   * in cards, grid and list alike. A new plant used to land wherever its sort
   * put it, often at the foot of a long page, and looked as if it had not
   * been saved. On the next visit it takes its ordinary place.
   */
  const arrangedPlants = useMemo(() => arrangePlants(pool, settings, attention), [pool, settings, attention]);
  // B102: a plant she just changed stays where it was drawn until the sort or view changes.
  const keptPlants = useKeepInPlace(arrangedPlants, JSON.stringify(settings));
  const shownPlants = useMemo(() => {
    const arranged = keptPlants;
    if (justAdded.length === 0) return arranged;
    const first = justAdded
      .map((id) => arranged.find((plant) => plant.plant_obj_id === id))
      .filter((plant): plant is Plant => plant !== undefined);
    return [...first, ...arranged.filter((plant) => !justAdded.includes(plant.plant_obj_id))];
  }, [keptPlants, justAdded]);

  /**
   * Whether to offer adding a plant at all.
   *
   * Not while the archive is what is on screen: the archive is where plants go
   * when they leave, and a new one would not appear in it, so the offer would
   * be a door into a different room.
   *
   * It stays while a filter is narrowing the garden, though. Searching for
   * "pea" and deciding to plant another one is a reasonable next thought, and
   * a control that comes and goes with a search box is harder to rely on than
   * one that is simply there.
   */
  const canAddPlant = settings.status !== 'archived' && (plants?.length ?? 0) > 0;

  /**
   * The date the current order is based on, ready to read — or null.
   *
   * Only when the sort is a date. No view showed the date "Recently updated"
   * was ordering by, so the order read as arbitrary. (The default order is
   * Needs attention, DEFAULT_VIEW_SETTINGS.)
   *
   * @param plant - The plant.
   */
  const sortedOn = (plant: Plant): string | null => {
    if (settings.sort !== 'recent') return null;
    const when = lastActivity(plant);
    return Number.isNaN(when) ? null : formatDate(parseDate(new Date(when).toISOString()));
  };

  /** The beds and stages the plants on offer use, for the sheet's choices and the chips' names. */
  const locationOptions = useMemo(() => locationsInUse(pool), [pool]);
  /** The stages the plants on offer are at. */
  const stageOptions = useMemo(() => stagesInUse(pool), [pool]);

  /*
   * Scroll to the plant named in the URL — once, on arrival.
   *
   * This used to depend on `plants` as well, so every refetch of the garden
   * scrolled the page again: saving a photo, editing a plant, or any
   * background refresh yanked the view back to the card in the address bar
   * while the gardener was reading something else. The URL is what says where
   * to go, and it only says it once.
   */
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    if (!plantObjId || scrolledTo.current === plantObjId) return;
    const targetCard = document.getElementById(`plant-card-${plantObjId}`);
    if (!targetCard) return;
    scrolledTo.current = plantObjId;
    targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [plantObjId, plants]);

  /**
   * Renders loading state when data is being fetched
   */
  if (isLoading) return <GrowLoader variant="screen" steps={[t('loader.plants.gather'), t('loader.plants.weather')]} />;

  /**
   * Renders error state when data fetch fails
   */
  if (error) return <div className={UI_CONSTANTS.GRID_STYLES.ERROR}><FailureText text={errorMessage(error, t('plants.list.loadFailed'))} /></div>;

  /**
   * Renders the plant grid
   * @returns {JSX.Element} A responsive grid of plant cards with proper layout and centering
   */
  return (
    /* pb, not py: the main element above already spaces the page from the
       header, and both paying for it put the garden twice as far down.
       max-w-6xl: on a wide desktop the bar, the toolbar and three columns of
       cards stay together instead of spreading across the whole screen.
       max-w-xl while there are no plants at all: the same column Today uses,
       so the bar, the toolbar and the two first steps are not stretched across
       a desktop with nothing under them.
       px-0 on a phone: the page's main already keeps the 16px gutter, and
       this one on top of it left the cards 32px from the screen's edge
       (325px wide at 393); from sm up it is its own px-4 as before. */
    <div className={cn(
      "mx-auto w-full px-0 pb-6 mb-6 sm:px-4",
      plants && plants.length === 0 ? "max-w-xl" : "max-w-6xl",
      textVariants({ font: "serif" })
    )}>
      {missingZipCode && <ZipRequiredBanner />}
      {/* The bar names what is coming for the garden this week and the plants
          each line is about; a tap hands the reader those plants. */}
      {/* The bar speaks for the garden's week whatever the grid below is
          showing — and a line clears the status filter on the way, which is
          how somebody reading the archive gets back to the plants the
          warning is actually about. */}
      {/* "Updating your garden..." while a fresh brief is on its way; the old week and jobs below look quieter until it arrives. */}
      <GuidanceUpdating where="Plants" className="mb-3" />
      <GuidanceQuiet>
      <GardenConditionsBar
        lines={weekLines}
        strip={brief?.strip.text || null}
        weekState={weekState}
        activeLine={activeLine}
        // A line narrows the garden to its plants without clearing the rest,
        // apart from the archive: nothing there is out in this week's weather.
        onToggleLine={(line) => updateSettings(activeLine?.id === line.id ? NO_LINE_FILTER : weekLineFilter(line))}
        // The week is said by the jobs under it, by bed (owner, 2026-10-16: "didnt we change this?" of the week's lines,
        // which the audit fix daa1600 had put back).
        weatherOnly={todo !== null && attentionApplies}
      />

      {/* The week as jobs (D51): the same cards as Today, plant jobs only; a tap shows just a job's plants. */}
      {todo && attentionApplies && (
        <PlantJobs
          todo={todo}
          marks={todoMarks}
          activeKey={activeJob?.key ?? null}
          onSelect={(job) => {
            if (!job.filter) return;
            updateSettings(activeJob?.key === job.key ? NO_LINE_FILTER : weekLineFilter({ plantIds: job.filter.plantIds, title: job.filter.text }));
          }}
          onShowAll={() => updateSettings(NO_LINE_FILTER)}
        />
      )}
      </GuidanceQuiet>

      {/* Offered once there is enough garden to get lost in. Three tomatoes do
          not need a search box, and an empty toolbar is furniture. */}
      {/* Always offered now: the status filter is the only way to the archive,
          so it cannot be hidden behind "you have enough plants to need it". */}
      <PlantToolbar
        settings={settings}
        onChange={updateSettings}
        locations={locationOptions}
        stages={stageOptions}
        shown={shownPlants.length}
        total={pool?.length ?? 0}
        marks={marks}
        lines={weekLines}
        hidePlantsChip={barNamesLine}
      />

      {/* An empty garden gets both ways in. The greeting and the first step
          are on Today, where Grow opens (the tips). */}
      {plants && plants.length === 0 && (
        <EmptyGarden message={t('plants.noPlants')}>
          {/* The pop-up's two rows in place (D78 P1): a photo of what is growing, or one plant typed. */}
          <AddWays {...ADD_PLANT_WORDS} onPhoto={addPlant.photo} onType={addPlant.type} />
        </EmptyGarden>
      )}

      {/* Filtered down to nothing is a different thing from having no plants,
          and saying "No plants found" to someone with forty would be a lie. */}
      {plants && plants.length > 0 && shownPlants.length === 0 && (
        <p className="mx-auto max-w-md text-center text-muted-foreground">{t('plants.toolbar.noMatches')}</p>
      )}

      {/*
        * Three ways to read the same garden.
        *
        * The card is the richest and the tallest — photos, forecast, journal —
        * which makes it the wrong shape once there are more plants than fit on
        * a screen. The tile and the row carry the same identity in a fraction
        * of the height and hand off to the timeline for the rest.
        *
        * Every plant keeps its plant-card-<id> anchor whichever view is
        * showing, so a link straight to one plant still scrolls to it.
        */}
      {settings.view === 'cards' && (
        /*
         * Three columns, and every card the width of its column.
         *
         * This carried justify-items-center, which sizes each grid item to its
         * own content rather than to the column. The cards ask for w-full, so
         * "full" became "as wide as whatever is inside me" and no two cards
         * came out the same width. Centring is done by the wrapper below
         * instead, which leaves the column width to the grid where it belongs.
         */
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 content-start">
          {/*
            * First, not last.
            *
            * It used to sit at the end of the garden, behind every plant in
            * it, while a button above the toolbar did the same job — two ways
            * to do one thing, and the nearer of them buried. The button was
            * never pinned, so it scrolled off the moment you moved down the
            * page; it was not the reliable one either. One offer, at the front,
            * where a new plant will actually appear.
            */}
          {canAddPlant && (
            <div className="flex w-full flex-col items-center">
              <AddPlaceholder shape="card" label={t('plants.addPlant')} onClick={openAdd} />
            </div>
          )}

          {shownPlants.map((plant: Plant) => (
            <div
              key={plant.plant_obj_id}
              id={`plant-card-${plant.plant_obj_id}`}
              className="flex w-full flex-col items-center"
            >
              {/* An archived plant keeps its card — the photographs and the
                  journal are the reason archiving is not deleting — with the
                  one thing you would want to do to it on top. */}
              {archivedIds.has(plant.plant_obj_id) && (
                <div className="mb-2 flex w-full max-w-sm items-center justify-between rounded-sm border border-warning/40 bg-warning-tint px-3 py-1.5 text-sm">
                  <span>{t('plants.toolbar.statusArchived')}</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void handleRestore(plant)}
                      disabled={restoringId === plant.plant_obj_id}
                      className="flex items-center gap-1 font-medium hover:underline disabled:opacity-50"
                    >
                      <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('plants.archive.restore')}
                    </button>
                    <button
                      type="button"
                      onClick={() => askDelete(plant)}
                      disabled={restoringId === plant.plant_obj_id}
                      className="flex items-center gap-1 font-medium text-destructive hover:underline disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('plants.form.deleteForeverEllipsis')}
                    </button>
                  </div>
                </div>
              )}
              <PlantCard
                plant={plant}
                onEdit={archivedIds.has(plant.plant_obj_id) ? undefined : openEdit}
                archived={archivedIds.has(plant.plant_obj_id)}
                sublabel={sortedOn(plant)}
              />
            </div>
          ))}

        </div>
      )}

      {settings.view === 'grid' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {canAddPlant && (
            <AddPlaceholder shape="tile" label={t('plants.addPlant')} onClick={openAdd} />
          )}

          {shownPlants.map((plant: Plant) => (
            <div key={plant.plant_obj_id} id={`plant-card-${plant.plant_obj_id}`}>
              <PlantTile
                plant={plant}
                onOpen={setOpened}
                sortedOn={sortedOn(plant)}
                archived={archivedIds.has(plant.plant_obj_id)}
                onRestore={(p) => void handleRestore(p)}
                onDelete={askDelete}
                isRestoring={restoringId === plant.plant_obj_id}
              />
            </div>
          ))}

        </div>
      )}

      {settings.view === 'list' && (
        <div className="divide-y rounded-sm border bg-card">
          {canAddPlant && (
            <AddPlaceholder shape="row" label={t('plants.addPlant')} onClick={openAdd} />
          )}

          {shownPlants.map((plant: Plant) => (
            <div key={plant.plant_obj_id} id={`plant-card-${plant.plant_obj_id}`}>
              <PlantRow
                plant={plant}
                onOpen={setOpened}
                sortedOn={sortedOn(plant)}
                archived={archivedIds.has(plant.plant_obj_id)}
                onRestore={(p) => void handleRestore(p)}
                onDelete={askDelete}
                isRestoring={restoringId === plant.plant_obj_id}
              />
            </div>
          ))}

        </div>
      )}

      {/* No onEdit: the quick look carries its own now, so the locations page
          gets the same pencil without having to know to ask for it. */}
      <PlantQuickLook plant={openedPlant} onClose={() => setOpened(null)} />

      {/* "Share your plant?" once, and the photo screen the add form offers. */}
      <ShareMoments plants={plants ?? []} />

      {/* "Did it turn out to be …?", once, about a week after a check-up. */}
      <DoctorMoments plants={plants ?? []} />

      {deleting && (
        <DeletePlantDialog
          isOpen
          onOpenChange={(open) => { if (!open) setDeleting(null); }}
          plantName={plantName(deleting)}
          photos={deleting.photos?.length ?? 0}
          notes={deleting.notes?.length ?? 0}
          busy={deleteBusy}
          error={deleteError}
          onConfirm={() => { void handleDeleteForever(); }}
        />
      )}

      <MergePlantSheet {...merge.sheetProps} />

      {addPlant.element}

      <PlantFormDialog
        plant={editing}
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); merge.afterFormClosed(); }}
        onSave={handleSave}
        onRemove={handleRemove}
        // Merge into... is in the form's Archive panel for a plant with twins: the form closes and the sheet opens.
        onMerge={(p) => { setIsFormOpen(false); merge.openMerge(p); }}
      />
    </div>
  );
});

PlantGrid.displayName = 'PlantGrid';